/**
 * Invoices Hook
 * Manages invoice operations with backend integration
 * Matching mobile's implementation
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { apiClient } from '@/shared/services/api';
import { API_ROUTES } from '@/shared/services/apiRoutes';

export type InvoiceStatus =
  | 'DRAFT'
  | 'SENT'
  | 'VIEWED'
  | 'PROCESSING'
  | 'PAID'
  | 'FAILED'
  | 'REFUNDED'
  | 'CANCELED';

export interface Invoice {
  id: string;
  invoice_number: string;
  customer_id: string | null;
  customer_name: string;
  amount_cents: number;
  currency: string;
  status: InvoiceStatus;
  title?: string | null;
  notes?: string | null;
  created_at: string;
  paid_at?: string | null;
  company_id?: string | null;
  // Fee fields
  fee_tier?: 'split_fee' | 'hybrid' | 'absorbed' | null;
  customer_total_cents?: number | null;
  technology_fee_cents?: number | null;
  merchant_fee_cents?: number | null;
  // Refund fields
  refunded_amount_cents?: number | null;
  refunded_at?: string | null;
  refund_reason?: string | null;
  fwd_refund_id?: string | null;
  // Payment fields
  fwd_payment_intent_id?: string | null;
  payment_url?: string | null;
  metadata?: Record<string, any> | null;
  // Void fields
  canceled_at?: string | null;
  voided_amount_cents?: number | null;
}

export interface CreateInvoiceInput {
  customer_id?: string;
  customer_name: string;
  customer_first_name?: string;
  customer_last_name?: string;
  amount_cents: number;
  currency?: string;
  title?: string;
  notes?: string;
  fee_tier?: 'split_fee' | 'hybrid' | 'absorbed';
  postal_code?: string;
  send_sms?: boolean;
}

const ITEMS_PER_PAGE = 50;

export function useInvoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [merchant, setMerchant] = useState<any>(null);

  // Paid invoices from backend API (accurate, queries DB directly)
  const [paidLast7DaysFromBackend, setPaidLast7DaysFromBackend] = useState<number>(0);
  const [paidInvoicesLast7Days, setPaidInvoicesLast7Days] = useState<Invoice[]>([]);

  const { user } = useAuth();

  /**
   * Fetch paid invoices from backend (for accurate 7-day metrics)
   */
  const fetchPaidInvoices = useCallback(async () => {
    try {
      const response = await apiClient.get<{
        ok: boolean;
        invoices: Invoice[];
        totalCents: number;
        count: number;
        days: number;
      }>(`${API_ROUTES.INVOICES}/paid?days=7`);

      if (response.ok) {
        setPaidLast7DaysFromBackend(response.totalCents || 0);
        setPaidInvoicesLast7Days(response.invoices || []);
      }
    } catch (e) {
      console.warn('[useInvoices] Failed to load paid invoices:', e);
      // Don't set error - this is supplementary data
    }
  }, []);

  /**
   * Fetch invoices from backend or Supabase
   */
  const fetchInvoices = useCallback(async (loadMore = false) => {
    console.log('[useInvoices] fetchInvoices called with user:', user);

    if (!user?.company_id) {
      console.log('[useInvoices] No company_id, returning early');
      setInvoices([]);
      setLoading(false);
      return;
    }

    try {
      if (!loadMore) {
        setLoading(true);
      }
      setError(null);

      const currentOffset = loadMore ? offset : 0;

      // SECURITY: Use backend API for all invoice operations
      console.log('[useInvoices] Fetching from backend API');

      const response = await apiClient.get<{
        ok: boolean;
        invoices: Invoice[];
        total: number;
        hasMore: boolean;
        merchant?: any;
      }>(`${API_ROUTES.INVOICES}?offset=${currentOffset}&limit=${ITEMS_PER_PAGE}`);

      if (!response.ok) throw new Error('Failed to fetch invoices');

      // Store merchant data from backend response (source of truth)
      // Backend always returns merchant (even if null) in the response
      setMerchant(response.merchant || null);

      if (loadMore) {
        setInvoices(prev => [...prev, ...(response.invoices || [])]);
      } else {
        setInvoices(response.invoices || []);
      }

      setHasMore(response.hasMore);
      setOffset(currentOffset + ITEMS_PER_PAGE);
    } catch (err: any) {
      // Silently handle schema errors (backend database not ready)
      if (err.message?.includes('does not exist') || err.message?.includes('column')) {
        console.warn('[useInvoices] Database schema issue - invoices feature not available yet');
        setError(null); // Don't show error to user
        setInvoices([]); // Just show empty state
      } else {
        console.error('[useInvoices] Error:', err);
        setError(err.message || 'Failed to fetch invoices');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, offset]);

  /**
   * Load more invoices (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchInvoices(true);
  }, [hasMore, loading, fetchInvoices]);

  /**
   * Refresh invoices
   */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    setOffset(0);
    await Promise.all([
      fetchInvoices(false),
      fetchPaidInvoices(), // Also refresh paid invoices
    ]);
  }, [fetchInvoices, fetchPaidInvoices]);

  /**
   * Create invoice (matching mobile app implementation)
   */
  const createInvoice = useCallback(
    async (input: CreateInvoiceInput): Promise<Invoice | null> => {
      if (!user?.company_id) {
        setError('No company ID');
        return null;
      }

      try {
        setError(null);

        // SECURITY: Use backend API for creating invoices
        console.log('[useInvoices] Creating invoice via backend API');

        // Get merchant from state (already loaded)
        if (!merchant) {
          throw new Error('Merchant account not found. Please complete merchant onboarding.');
        }

        if (merchant.status !== 'ACTIVE') {
          throw new Error('Merchant account is not active. Please complete onboarding.');
        }

        console.log('[useInvoices] Creating invoice with merchant_id:', merchant.id);

        // Call backend API
        const response = await apiClient.post<{ invoice: Invoice }>('/payments/create-invoice', {
          merchant_id: merchant.id,
          customer_id: input.customer_id,
          customer_name: input.customer_name,
          customer_first_name: input.customer_first_name,
          customer_last_name: input.customer_last_name,
          amount_cents: input.amount_cents,
          currency: input.currency || 'USD',
          title: input.title,
          notes: input.notes,
          send_sms: true,
        });

        // Refresh list
        await refresh();

        return response?.invoice || null;
      } catch (err: any) {
        console.error('[useInvoices] Error creating invoice:', err);
        setError(err.message || 'Failed to create invoice');
        return null;
      }
    },
    [user?.company_id, merchant, refresh]
  );

  /**
   * Refund invoice (full or partial)
   */
  const refundInvoice = useCallback(
    async (invoice_id: string, refund_amount_cents: number, reason?: string): Promise<boolean> => {
      try {
        setError(null);
        const response = await apiClient.post<{ ok: boolean; error?: string }>(
          '/payments/refund-invoice',
          { invoice_id, refund_amount_cents, reason }
        );
        if (!response.ok) throw new Error(response.error || 'Refund failed');
        await refresh();
        return true;
      } catch (err: any) {
        console.error('[useInvoices] Error refunding invoice:', err);
        setError(err.message || 'Failed to refund invoice');
        return false;
      }
    },
    [refresh]
  );

  const voidInvoice = useCallback(
    async (invoice_id: string): Promise<boolean> => {
      try {
        setError(null);
        const response = await apiClient.post<{ ok: boolean; error?: string }>(
          '/payments/void-invoice',
          { invoice_id }
        );
        if (!response.ok) throw new Error(response.error || 'Void failed');
        await refresh();
        return true;
      } catch (err: any) {
        console.error('[useInvoices] Error voiding invoice:', err);
        setError(err.message || 'Failed to void invoice');
        return false;
      }
    },
    [refresh]
  );

  /**
   * Calculate metrics
   */
  const unpaidTotalCents = invoices
    .filter(inv => ['SENT', 'VIEWED', 'PROCESSING'].includes(inv.status))
    .reduce((sum, inv) => sum + inv.amount_cents, 0);

  const paidTotalCents = invoices
    .filter(inv => inv.status === 'PAID')
    .reduce((sum, inv) => sum + inv.amount_cents, 0);

  const last7Days = new Date();
  last7Days.setDate(last7Days.getDate() - 7);

  // Use backend value for accurate paid last 7 days
  const paidLast7DaysCents = paidLast7DaysFromBackend;

  const sentLast7DaysCents = invoices
    .filter(inv =>
      inv.status === 'SENT' &&
      new Date(inv.created_at) >= last7Days
    )
    .reduce((sum, inv) => sum + inv.amount_cents, 0);

  // Use backend data for recent paid invoices
  const recentPaidInvoices = paidInvoicesLast7Days.slice(0, 10);

  // Initial fetch
  useEffect(() => {
    fetchInvoices(false);
    fetchPaidInvoices(); // Also fetch paid invoices for accurate 7-day metrics
  }, [user?.company_id]); // Only depend on company_id change

  return {
    invoices,
    loading,
    refreshing,
    error,
    hasMore,
    refresh,
    loadMore,
    createInvoice,
    refundInvoice,
    voidInvoice,
    merchant, // Export merchant data (matching mobile app)
    // Metrics
    unpaidTotalCents,
    paidTotalCents,
    paidLast7DaysCents,
    sentLast7DaysCents,
    recentPaidInvoices,
  };
}
